// GtClientFacts — the "Company data" card on a client record (C1-CLIENTENRICH).
//
// Intelligence fills it: Enrich reads the client's website, company profiles
// and funding news, and each value links to the page it came from. It also
// runs by itself the first time a client with a website is opened, and after
// every Intelligence sweep. Values a person types are never overwritten.
// Changes over time show underneath ("Series A to Series B").
import { useState, useEffect, useCallback, useRef } from 'react';

function ukDay(v) {
  if (!v) return '';
  const d = new Date(String(v).length === 10 ? v + 'T12:00:00Z' : v);
  return isNaN(d) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
function ukWhen(v) {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d)) return '';
  const today = new Date();
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === today.toDateString()) return 'today at ' + time;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ' at ' + time;
}
function shown(f) {
  if (!f) return null;
  // Dates show as the source gave them ("April 2024"), never with an invented day.
  if (f.type === 'date' && f.value_date && /^\d{4}-\d{1,2}-\d{1,2}/.test(String(f.value_text || ''))) return ukDay(f.value_date);
  return f.value_text || null;
}
function host(url) { try { return new URL(url).hostname.replace(/^www\./, ''); } catch (e) { return null; } }
function whereFrom(f) {
  if (!f) return '';
  if (f.source === 'manual') return 'Typed by ' + (f.updated_by_name || 'a person') + (f.as_of ? ', ' + ukDay(f.as_of) : '');
  if (f.source === 'signal') return 'Found by Intelligence on ' + (host(f.source_url) || 'the web') + (f.as_of ? ', ' + ukDay(f.as_of) : '');
  return 'From ' + (f.source_label || 'an import') + (f.as_of ? ', ' + ukDay(f.as_of) : '');
}

const PLACEHOLDER = {
  latest_funding: 'e.g. Series B', latest_funding_amount: 'e.g. $35M', last_raised_at: 'e.g. March 2025',
  total_funding: 'e.g. $60M', employees: 'e.g. 140 or 51-200', annual_revenue: 'e.g. £12M',
  industry: 'e.g. Cyber insurance', founded_year: 'e.g. 2019', description: 'What they do, in a sentence or two',
  keywords: 'Comma separated', technologies: 'Comma separated',
};
const TILES = [
  { key: 'latest_funding', label: 'Latest round', sub: 'latest_funding_amount' },
  { key: 'total_funding', label: 'Total funding' },
  { key: 'employees', label: 'Employees' },
  { key: 'founded_year', label: 'Founded' },
];
const PROPS = ['last_raised_at', 'industry', 'annual_revenue'];
const LISTS = ['keywords', 'technologies'];
const SHOWN = new Set(TILES.map((t) => t.key).concat(TILES.map((t) => t.sub).filter(Boolean), PROPS, LISTS, ['description']));

const Spark = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l1.8 4.7L18.5 9.5l-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8z" /><path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z" />
  </svg>
);
const Out = () => (
  <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 4h6v6" /><path d="M20 4l-9 9" /><path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
  </svg>
);
const Pen = () => (
  <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 20h4L19 9l-4-4L4 16z" />
  </svg>
);

// The small mark after a value: a link to its page, or a pen for typed values.
function SourceMark({ f }) {
  if (!f) return null;
  if (f.source === 'signal' && f.source_url) {
    return <a className="gtcfx2-src" href={f.source_url} target="_blank" rel="noreferrer" title={whereFrom(f)} aria-label={whereFrom(f)}><Out /></a>;
  }
  if (f.source === 'manual') return <span className="gtcfx2-src gtcfx2-src--pen" title={whereFrom(f)} aria-label={whereFrom(f)}><Pen /></span>;
  return null;
}

export default function GtClientFacts({ clientId, authFetch, hasWebsite }) {
  const [data, setData] = useState(null);
  const [enrich, setEnrich] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [showAll, setShowAll] = useState({});
  const autoTried = useRef(false);
  const polls = useRef(0);

  const load = useCallback(async () => {
    try {
      const [f, s] = await Promise.all([
        authFetch('/api/clients/' + clientId + '/facts'),
        authFetch('/api/clients/' + clientId + '/facts/enrich').catch(() => null),
      ]);
      setData(f); setEnrich(s); setError(null);
      return { f, s };
    } catch (e) { setError(e.message); return null; }
  }, [authFetch, clientId]);

  const startEnrich = useCallback(async (auto) => {
    setError(null);
    try {
      const s = await authFetch('/api/clients/' + clientId + '/facts/enrich', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ auto: !!auto }),
      });
      polls.current = 0;
      setEnrich(s);
    } catch (e) { if (!auto) setError(e.message); }
  }, [authFetch, clientId]);

  // First open of a client with a website and no company data: fill it.
  useEffect(() => {
    let live = true;
    (async () => {
      const got = await load();
      if (!live || !got || autoTried.current) return;
      autoTried.current = true;
      const facts = (got.f && got.f.facts) || [];
      if (got.s && got.s.status === 'never' && got.s.has_website && facts.length === 0) startEnrich(true);
    })();
    return () => { live = false; };
  }, [load, startEnrich]);

  // While a run is going, check every few seconds and refresh when it ends.
  const running = enrich && enrich.status === 'running';
  useEffect(() => {
    if (!running) return undefined;
    if (polls.current > 60) return undefined;
    const t = setTimeout(() => { polls.current += 1; load(); }, 3000);
    return () => clearTimeout(t);
  }, [running, enrich, load]);

  function startEdit() {
    const d = {};
    (data.known || []).forEach((k) => { d[k.key] = ''; });
    (data.facts || []).forEach((f) => { d[f.key] = f.value_text || ''; });
    setDraft(d); setEditing(true);
  }
  async function save() {
    setBusy(true); setError(null);
    try {
      const before = {};
      (data.facts || []).forEach((f) => { before[f.key] = f.value_text || ''; });
      for (const key of Object.keys(draft)) {
        if (String(draft[key]).trim() === String(before[key] || '').trim()) continue;
        await authFetch('/api/clients/' + clientId + '/facts/' + encodeURIComponent(key), {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ value: draft[key] }),
        });
      }
      setEditing(false);
      await load();
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  if (!data) {
    return (
      <div className="gtr-card gtcfx2">
        <div className="gtr-cardh">Company data</div>
        <div className="gtr2-empty">{error || 'Loading…'}</div>
      </div>
    );
  }

  const facts = data.facts || [];
  const byKey = {};
  facts.forEach((f) => { byKey[f.key] = f; });
  const known = data.known || [];
  const labelOf = {};
  known.forEach((k) => { labelOf[k.key] = k.label; });
  facts.forEach((f) => { labelOf[f.key] = f.label; });
  const history = data.history || [];
  const lastChange = {};
  history.forEach((h) => { if (!lastChange[h.key]) lastChange[h.key] = h; });
  const extras = facts.filter((f) => !SHOWN.has(f.key));
  const site = hasWebsite !== undefined ? hasWebsite : !!(enrich && enrich.has_website);

  const was = (key) => {
    const ch = lastChange[key];
    if (!ch || !ch.old_text) return null;
    return <em className="gtcfx2-was">was {ch.old_text.length > 36 ? ch.old_text.slice(0, 36) + '…' : ch.old_text}</em>;
  };

  // One line under the header saying what Intelligence has done.
  let status = null;
  if (running) {
    status = <div className="gtcfx2-status gtcfx2-status--run"><span className="gten-spin" aria-hidden="true" />Intelligence is reading their website, company profiles and news.</div>;
  } else if (enrich && enrich.status === 'failed') {
    status = <div className="gtcfx2-status gtcfx2-status--bad">{enrich.note || 'The last run did not finish.'}</div>;
  } else if (enrich && enrich.status === 'empty') {
    status = <div className="gtcfx2-status">{(enrich.note || 'Nothing found on the web.') + ' Checked ' + ukWhen(enrich.enriched_at) + '.'}</div>;
  } else if (enrich && enrich.status === 'done') {
    status = <div className="gtcfx2-status gtcfx2-status--ok"><Spark />Enriched {ukWhen(enrich.enriched_at)}. {enrich.note}</div>;
  } else if (!site) {
    status = <div className="gtcfx2-status">Add the company website in Client information and Intelligence will fill this in.</div>;
  }

  const enrichBtn = (
    <button type="button" className="gtr2-btn gtr2-btn--xs gtcfx2-enrich" disabled={running || busy || !site} onClick={() => startEnrich(false)}
      title={!site ? 'Add the company website first' : 'Find funding, headcount, industry and more on the web'}>
      <Spark />{running ? 'Enriching' : (enrich && enrich.status && enrich.status !== 'never' ? 'Re-enrich' : 'Enrich')}
    </button>
  );

  return (
    <div className="gtr-card gtcfx2">
      <div className="gtr-cardh">
        Company data
        {!editing ? (
          <span className="gtr2-panelacts">{enrichBtn}<button type="button" className="gtr-cardh-edit" onClick={startEdit}>Edit</button></span>
        ) : (
          <span className="gtr2-panelacts"><button type="button" className="gtr-cardh-edit gtr2-muted" disabled={busy} onClick={() => setEditing(false)}>Cancel</button><button type="button" className="gtr-cardh-edit" disabled={busy} onClick={save}>{busy ? 'Saving' : 'Save'}</button></span>
        )}
      </div>
      {error && <div className="gtcfx2-status gtcfx2-status--bad">{error}</div>}
      {!editing && status}

      {editing ? (
        <div className="gtr-editform gtcfx2-form">
          {(data.groups || []).map((g) => {
            const keys = Object.keys(draft).filter((k) => {
              const def = known.find((x) => x.key === k);
              return (def ? def.group : 'Other') === g;
            });
            if (!keys.length) return null;
            return (
              <fieldset key={g} className="gtcfx2-fs">
                <legend>{g}</legend>
                {keys.map((key) => (
                  <label key={key} className="gtc-field"><span>{labelOf[key] || key}</span>
                    {key === 'description' || LISTS.includes(key)
                      ? <textarea rows={key === 'description' ? 3 : 2} value={draft[key]} placeholder={PLACEHOLDER[key] || ''} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} />
                      : <input value={draft[key]} placeholder={PLACEHOLDER[key] || ''} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} />}
                  </label>
                ))}
              </fieldset>
            );
          })}
          <p className="gtcfx2-hint">Anything you type here is kept: Intelligence never overwrites it. Clearing a value removes it, and every change stays in the history.</p>
          <div className="gtr-editactions">
            <button type="button" className="gtc-primary" disabled={busy} onClick={save}>{busy ? 'Saving' : 'Save changes'}</button>
            <button type="button" className="gtc-colbtn" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <div className="gtcfx2-tiles">
            {TILES.map((t) => {
              const f = byKey[t.key];
              const sub = t.sub ? byKey[t.sub] : null;
              const v = shown(f);
              return (
                <div key={t.key} className={'gtcfx2-tile' + (v ? '' : ' gtcfx2-tile--none')} title={f ? whereFrom(f) : (running ? 'Looking' : 'Not known yet')}>
                  <span className="gtcfx2-tk">{t.label}</span>
                  <span className="gtcfx2-tv">{v || (running ? '…' : '—')}{f && <SourceMark f={f} />}</span>
                  {sub && shown(sub) && <span className="gtcfx2-ts">{shown(sub)}<SourceMark f={sub} /></span>}
                  {was(t.key)}
                </div>
              );
            })}
          </div>

          {PROPS.some((k) => byKey[k]) && (
            <div className="gtr-props gtcfx2-props">
              {PROPS.filter((k) => byKey[k]).map((k) => (
                <div key={k} className="gtr-prop" title={whereFrom(byKey[k])}>
                  <span className="gtr-prop-k">{labelOf[k]}</span>
                  <span className="gtr-prop-v gtcfx2-pv">{shown(byKey[k])}<SourceMark f={byKey[k]} />{was(k)}</span>
                </div>
              ))}
            </div>
          )}

          {byKey.description && (
            <p className="gtcfx2-desc" title={whereFrom(byKey.description)}>{byKey.description.value_text}<SourceMark f={byKey.description} /></p>
          )}

          {LISTS.filter((k) => byKey[k]).map((k) => {
            const items = String(byKey[k].value_text || '').split(', ').filter(Boolean);
            const open = !!showAll[k];
            return (
              <div key={k} className="gtcfx2-list" title={whereFrom(byKey[k])}>
                <div className="gtcfx2-lh">{labelOf[k]}<SourceMark f={byKey[k]} /></div>
                <div className="gtcfx-tags">
                  {items.slice(0, open ? 999 : 8).map((x) => <i key={x}>{x}</i>)}
                  {!open && items.length > 8 && <button type="button" className="gtcfx-more" onClick={() => setShowAll({ ...showAll, [k]: true })}>+{items.length - 8}</button>}
                </div>
              </div>
            );
          })}

          {extras.length > 0 && (
            <div className="gtr-props gtcfx2-props">
              {extras.map((f) => (
                <div key={f.key} className="gtr-prop" title={whereFrom(f)}>
                  <span className="gtr-prop-k">{f.label}</span>
                  <span className="gtr-prop-v gtcfx2-pv">{shown(f) || '—'}<SourceMark f={f} /></span>
                </div>
              ))}
            </div>
          )}

          {facts.length === 0 && !running && site && (!enrich || enrich.status === 'never') && (
            <div className="gtr2-empty">No company data yet. Press Enrich and Intelligence will look for funding, headcount, industry and what they do.</div>
          )}

          {history.length > 0 && (
            <details className="gtcfx-hist">
              <summary>Changes over time ({history.length})</summary>
              <ul>
                {history.slice(0, 30).map((h) => (
                  <li key={h.id}><b>{h.label}</b>: {h.old_text || 'nothing'} → {h.new_text || 'removed'}<span> · {ukDay(h.changed_at)}{h.source_label ? ' · ' + h.source_label : ''}</span></li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </div>
  );
}
